<template>
    <Overlay :show="showLoading || loading">
        <div>
            <app-collapse
                type="border"
                class="mb-1"
            >
                <app-collapse-item
                    v-for="list in substanceLists"
                    :key="list.id"
                    title=""
                    card-body-class="p-0"
                    :class="{'color-default-important': false !== list.questionAnswer}"
                    :is-visible="autoExpandLists || expandLists"
                    :is-collapsible="false"
                >
                    <template #header>
                        <div class="lead collapse-title">
                            <span v-if="list.hasReferencedLists">
                                {{ list.referencedLists.join(' & ') }} /
                            </span>
                            {{ list.title }}
                            <InstructionInfoButton
                                v-if="isTranslationSet('Component(IButton):ce-substance-list-instruction-' + list.key)"
                                custom-class="popover-wide"
                                class="ml-1"
                                :button="false"
                                :instruction="$t('Component(IButton):ce-substance-list-instruction-' + list.key)"
                            />
                        </div>
                        <div
                            v-if="list.hasQuestion"
                            class="d-flex justify-content-end align-items-center"
                        >
                            <div class="pr-1">{{ list.question }}</div>
                            <div class="demo-inline-spacing" @click.stop>
                                <b-form-radio
                                    class="mt-0"
                                    :name="getQuestionRadioName(list)"
                                    :value="true"
                                    :disabled="readOnly"
                                    v-model="list.questionAnswer"
                                    @click.native="rememberQuestionAnswer(list)"
                                    @change="questionAnswered(list, true)"
                                >
                                    {{ $t('Component(Choice):yes') }}
                                </b-form-radio>
                                <b-form-radio
                                    class="mt-0"
                                    :name="getQuestionRadioName(list)"
                                    :value="false"
                                    :disabled="readOnly || list.noQuestionDisabled"
                                    v-model="list.questionAnswer"
                                    @click.native="rememberQuestionAnswer(list)"
                                    @change="questionAnswered(list, false)"
                                >
                                    {{ $t('Component(Choice):no') }}
                                </b-form-radio>
                            </div>
                        </div>
                    </template>
                    <InstructionText
                        class="m-1"
                        v-if="isListInstructionPresented(list.key)"
                    >
                        {{ listInstruction(list.key) }}
                    </InstructionText>
                </app-collapse-item>
            </app-collapse>
        </div>
    </Overlay>
</template>

<script>
import { BFormRadio } from 'bootstrap-vue';
import Overlay from "@core/components/common/Overlay.vue";
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue';
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue';
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import InstructionInfoButton from "@core/components/common/instructions/InstructionInfoButton.vue";
import ActiveSubstanceListsMixin from "@/views/product/components/product-card/ActiveSubstanceListsMixin";
import {getAllSubstanceLists} from "@core/domain/substance";
import {SubstanceApi} from "@core/api";

export default {
    components: {
        AppCollapse,
        AppCollapseItem,
        BFormRadio,
        InstructionInfoButton,
        InstructionText,
        Overlay,
    },
    mixins: [ActiveSubstanceListsMixin],
    props: {
        product: {
            required: true
        },
        request: {
            default: null
        },
        legislations: {
            type: Array,
            default() {
                return null;
            }
        },
        includedListIds: {
            default() {
                return null;
            }
        },
        includedSubstances: {
            type: Array,
            default() {
                return [];
            }
        },
        loading: {
            default: false
        },
        readOnly: {
            type: Boolean,
            default: false
        },
        collapseQuestionlessReferencedLists: {
            type: Boolean,
            default: false
        },
        radioNamePrefix: {
            type: String,
            default: 'question-for'
        }
    },
    data() {
        return {
            expandLists: false,
            showLoading: true,
            substanceLists: [],
            fetchSubstanceListsPromise: null,
        };
    },
    computed: {
        activeLegislations() {
            if (Array.isArray(this.legislations)) {
                return this.legislations;
            }

            if (this.request && Array.isArray(this.request.legislations)) {
                return this.request.legislations.map(l => l.id);
            }

            return [];
        },
        autoExpandLists() {
            return this.activeLegislations.length === 1;
        },
        includedSubstanceIds() {
            return this.includedSubstances.map(container => container.substance.id);
        }
    },
    methods: {
        getQuestionRadioName(list) {
            return this.radioNamePrefix + '-' + list.id;
        },
        rememberQuestionAnswer(list) {
            if (this.readOnly) {
                return;
            }
            list.previousQuestionAnswer = list.questionAnswer;
        },
        questionAnswered(list, answer) {
            if (this.readOnly) {
                return;
            }

            if (answer === true && !list.hasIncludedSubstances) {
                list.questionAnswer = list.previousQuestionAnswer === undefined ? null : list.previousQuestionAnswer;
                this.$swal({
                    title: this.$t('Page(ReceivedRequestFMDFlow):Component(Swal):substance-list-yes-blocked-title'),
                    text: this.$t('Page(ReceivedRequestFMDFlow):Component(Swal):substance-list-yes-blocked-text'),
                    icon: 'info',
                    confirmButtonText: 'OK',
                    customClass: {
                        confirmButton: 'btn btn-primary',
                    },
                    buttonsStyling: false,
                });
                return;
            }

            if (list.questionAnswer !== answer) {
                list.questionAnswer = answer;
            }

            if (list.hasQuestion) {
                this.$emit('listQuestionStateUpdated', {id: list.id, title: list.title}, answer);
            }
        },
        normalizeIncludedResponse(data) {
            if (!data || !Array.isArray(data.items)) {
                return [];
            }

            return data.items.filter(item => item && item.included === true);
        },
        syncListStateWithIncludedSubstances(list) {
            list.noQuestionDisabled = false;
            list.hasIncludedSubstances = false;

            if (!list.hasQuestion) {
                return Promise.resolve();
            }

            const questionStates = this.product && this.product.substanceListQuestionStates
                ? this.product.substanceListQuestionStates
                : [];
            const existingState = questionStates.find(state => String(state.list) === String(list.id));
            list.questionAnswer = existingState ? existingState.state : null;

            if (Array.isArray(this.includedListIds)) {
                const includedListIds = this.includedListIds.map(id => String(id));
                const hasIncludedSubstances = list.effectiveIncludedListIds
                    .map(id => String(id))
                    .some(id => includedListIds.includes(id));

                list.hasIncludedSubstances = hasIncludedSubstances;
                list.noQuestionDisabled = hasIncludedSubstances;

                if (hasIncludedSubstances) {
                    list.questionAnswer = true;
                } else if (list.questionAnswer === true) {
                    list.questionAnswer = null;
                }

                return Promise.resolve();
            }

            if (this.includedSubstanceIds.length === 0) {
                return Promise.resolve();
            }

            list.checkingSubstances = true;

            return SubstanceApi
                .listSubstancesInclusion(list.id, this.includedSubstanceIds)
                .then(response => {
                    const included = this.normalizeIncludedResponse(response.data);
                    const hasIncludedSubstances = included.length > 0;

                    list.hasIncludedSubstances = hasIncludedSubstances;
                    list.noQuestionDisabled = hasIncludedSubstances;

                    if (hasIncludedSubstances) {
                        list.questionAnswer = true;
                    } else if (list.questionAnswer === true) {
                        list.questionAnswer = null;
                    }
                })
                .finally(() => {
                    list.checkingSubstances = false;
                });
        },
        refreshListStates() {
            if (this.substanceLists.length === 0) {
                this.$emit('validityChanged', true);
                return Promise.resolve();
            }

            this.showLoading = true;

            return Promise
                .all(this.substanceLists.map(list => this.syncListStateWithIncludedSubstances(list)))
                .finally(() => {
                    this.showLoading = false;
                    this.$emit('validityChanged', true);
                });
        },
        fetchSubstanceLists() {
            this.showLoading = true;

            if (this.fetchSubstanceListsPromise) {
                return this.fetchSubstanceListsPromise;
            }

            return this.fetchSubstanceListsPromise = getAllSubstanceLists()
                .then(lists => this.activeSubstanceLists(lists, this.activeLegislations))
                .then(lists => {
                    const idToList = {};
                    lists.forEach(list => {
                        idToList[list.getId()] = list;
                    });

                    const referencedListReferencingListsMap = {};
                    const parentListReferencedListsMap = {};
                    lists.forEach(parentList => {
                        parentList.getReferencingLists().forEach(referencedListId => {
                            const referencedList = idToList[referencedListId];
                            if (!referencedList) {
                                return;
                            }

                            if (!referencedListReferencingListsMap[referencedList.id]) {
                                referencedListReferencingListsMap[referencedList.id] = [];
                            }

                            referencedListReferencingListsMap[referencedList.id].push(parentList);

                            if (!parentListReferencedListsMap[parentList.id]) {
                                parentListReferencedListsMap[parentList.id] = [];
                            }

                            parentListReferencedListsMap[parentList.id].push(referencedList);
                        });
                    });

                    const hiddenReferencedListIds = [];
                    if (this.collapseQuestionlessReferencedLists) {
                        lists.forEach(list => {
                            const referencingLists = referencedListReferencingListsMap[list.id] || [];
                            const isReferencedByQuestionList = referencingLists.some(parentList => parentList.hasQuestion());

                            if (!list.hasQuestion() && isReferencedByQuestionList) {
                                hiddenReferencedListIds.push(list.id);
                            }
                        });
                    }

                    this.substanceLists = lists
                        .filter(list => !hiddenReferencedListIds.includes(list.id))
                        .map(list => {
                            const dto = list.toDTO();
                            const referencedLists = parentListReferencedListsMap[list.id] || [];
                            const collapsedReferencedListIds = referencedLists
                                .filter(referencedList => hiddenReferencedListIds.includes(referencedList.id))
                                .map(referencedList => referencedList.id);

                            dto.hasQuestion = list.hasQuestion();
                            dto.question = list.question;
                            dto.questionAnswer = null;
                            dto.noQuestionDisabled = false;
                            dto.hasIncludedSubstances = false;
                            dto.previousQuestionAnswer = null;
                            dto.checkingSubstances = false;
                            dto.hasReferencedLists = referencedListReferencingListsMap.hasOwnProperty(list.id);
                            dto.referencedLists = dto.hasReferencedLists
                                ? referencedListReferencingListsMap[list.id].map(parentList => parentList.title)
                                : [];
                            dto.effectiveIncludedListIds = [list.id].concat(collapsedReferencedListIds);

                            return dto;
                        });

                    if (this.substanceLists.length === 1) {
                        this.expandLists = true;
                    }

                    return this.refreshListStates();
                })
                .finally(() => {
                    this.fetchSubstanceListsPromise = null;
                    this.showLoading = false;
                    this.$emit('loaded');
                });
        },
        isTranslationSet(key) {
            const translation = this.$t(key);

            return translation !== key && translation.length > 0;
        },
        isListInstructionPresented(listKey) {
            return this.isTranslationSet('Component(ConcernElements):substance-list-instruction-' + listKey);
        },
        listInstruction(listKey) {
            return this.$t('Component(ConcernElements):substance-list-instruction-' + listKey);
        }
    },
    watch: {
        product: {
            handler() {
                this.fetchSubstanceLists();
            },
            immediate: true
        },
        includedListIds() {
            this.refreshListStates();
        },
        includedSubstances() {
            this.refreshListStates();
        }
    }
}
</script>
